import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useSession } from '@/auth/session'
import { useSelfProfile } from '@/auth/useSelfProfile'
import { Wordmark } from '@/auth/SessionProvider'
import { CurrencyReadout } from '@/components/CurrencyReadout'
import { Meter } from '@/components/Meter'
import { AvatarMenu } from './AvatarMenu'

/** Heat ink: cool → contested → wanted. */
function heatTone(heat: number, cap: number) {
  const r = cap > 0 ? heat / cap : 0
  if (r >= 0.8) return 'danger' as const
  if (r >= 0.4) return 'warning' as const
  return 'chrome-dark' as const
}

export function TopBar() {
  const { t } = useTranslation()
  const seed = useSession((s) => s.player)
  const demo = useSession((s) => s.demo)
  const { data } = useSelfProfile()
  const p = data ?? seed
  const heatCap = p && p.heatCapacity > 0 ? p.heatCapacity : 100

  return (
    <header className="relative z-30 shrink-0 bg-bg-deep">
      <div className="flex h-[58px] items-center gap-4 px-4 lg:gap-6">
        <Link to="/map" className="flex items-baseline gap-3">
          <Wordmark />
          <span className="soc-label hidden text-brass xl:inline">{t('auth.handlerMode')}</span>
        </Link>
        {demo && (
          <span className="soc-label border border-brass-dim px-2 py-1 text-brass" data-testid="demo-badge">
            {t('shell.demo')}
          </span>
        )}
        <div className="flex-1" />
        {p && (
          <div className="flex items-center gap-4 lg:gap-6">
            <CurrencyReadout currency="dirty" amount={p.dirtyCash} />
            <CurrencyReadout currency="clean" amount={p.cleanCash} className="hidden sm:inline" />
            <CurrencyReadout currency="influence" amount={p.cleanInfluence} className="hidden md:inline" />
            <div className="hidden w-[120px] lg:block" title={t('shell.heatTitle', { heat: p.heat, cap: heatCap })}>
              <div className="flex items-baseline justify-between">
                <span className="soc-label">{t('shell.heat')}</span>
                <span className="soc-num text-[12px] text-ink-soft">
                  {p.heat}/{heatCap}
                </span>
              </div>
              <Meter value={p.heat} max={heatCap} tone={heatTone(p.heat, heatCap)} className="mt-1" label={t('shell.heat')} />
            </div>
            <div className="flex flex-col items-center leading-none" title={t('shell.level')}>
              <span className="soc-label text-[9px]">{t('shell.levelShort')}</span>
              <span className="soc-num mt-1 text-[17px] text-ink">{p.level}</span>
            </div>
          </div>
        )}
        <AvatarMenu player={p ?? null} />
      </div>
      <div className="soc-plate-rule" />
    </header>
  )
}
